// run statuses come from the platform in title case
/* eslint-disable sentence-case/sentence-case */
import React, { useRef, useEffect, useState } from 'react'
import {
    ListContextProvider,
    Datagrid,
    TextField,
    FunctionField,
    useRecordContext,
    useListController,
    useListFilterContext,
    Filter,
} from 'react-admin'
import { Box, CircularProgress, Grid, Button } from '@mui/material'
import { CheckCircle, Error } from '@mui/icons-material'
import RunExpand from './RunExpand'
import { get } from 'lodash-es'
import ListPagination from '../List/ListPagination'
import { formatDate } from '@thefront/pandipackV2'
import { useThemeConfig } from '../../themeConfig'
import dayjs from 'dayjs'
import {
    EmptyState,
    FilterDateTimeInput,
    FilterSelectInput,
    IdField,
} from '../../components/common'

const RUNS_PER_PAGE = 20

const RUN_STATUS_PHASES = [
    {
        id: '["In Progress","Initializing","Automatic Retry"]',
        name: 'In Progress',
    },
    { id: '["Succeeded"]', name: 'Complete' },
    {
        id:
            '["Failed (Integration Issue)","Failed (Timeout)","Failed (Platform Issue)","Failed (Refresh)","Failed (Metadata Validation)"]',
        name: 'Error',
    },
]

const RunFilter = () => {
    const { filterValues, setFilters } = useListFilterContext()
    const { configs } = useThemeConfig()
    // the DateTimePickers need special state vars to hold the entire datetime object,
    // there is lag if the string that is stored in filterValues is passed to the value prop on the component.
    const [createdDateValue, setCreatedDateValue] = useState(
        filterValues.created_date__gt
            ? dayjs(filterValues.created_date__gt)
            : null
    )
    const [endDateValue, setEndDateValue] = useState(
        filterValues.created_date__lt
            ? dayjs(filterValues.created_date__lt)
            : null
    )
    return (
        <Box
            sx={{
                display: 'flex',
                justifyContent: 'flex-start',
                alignItems: 'center',
                marginY: 1,
                marginLeft: 1,
            }}
        >
            <Filter>
                <FilterSelectInput
                    source="trigger"
                    alwaysOn
                    choices={[
                        { id: 'cron', name: 'Cron' },
                        { id: 'webhook', name: 'Webhook' },
                        { id: 'manual', name: 'Manual' },
                        { id: 'api', name: 'API' },
                        { id: 'rerun', name: 'Rerun' },
                    ]}
                />
                <FilterSelectInput
                    source="status__phase__in"
                    label="Status"
                    alwaysOn
                    choices={RUN_STATUS_PHASES}
                />
                <Box
                    alwaysOn
                    sx={{
                        alignItems: 'center',
                        gap: 2,
                        display: 'flex',
                    }}
                >
                    <FilterDateTimeInput
                        source="created_date__gt"
                        label="Start"
                        filterValues={filterValues}
                        setFilters={setFilters}
                        value={createdDateValue}
                        setValue={setCreatedDateValue}
                        minValue={dayjs().subtract(1, 'month')}
                        maxValue={endDateValue}
                        format={configs.dateFormat}
                    />
                    <FilterDateTimeInput
                        source="created_date__lt"
                        label="End"
                        filterValues={filterValues}
                        setFilters={setFilters}
                        value={endDateValue}
                        setValue={setEndDateValue}
                        minValue={
                            createdDateValue || dayjs().subtract(1, 'month')
                        }
                        format={configs.dateFormat}
                    />
                </Box>
                <Button
                    onClick={() => {
                        setFilters({})
                        setCreatedDateValue(null)
                        setEndDateValue(null)
                    }}
                    alwaysOn
                    sx={{
                        letterSpacing: '1.5px',
                        mb: 1,
                    }}
                    disabled={Object.keys(filterValues).length === 0}
                >
                    Clear Filters
                </Button>
            </Filter>
        </Box>
    )
}

const runPhaseClasses = {
    initializing: { fontStyle: 'italic' },
    success: { color: '#7ED321' },
    error: { color: 'red', display: 'flex' },
    status: { padding: '0 20 0 0' },
}

const RunPhaseField = ({ source, addLabel = false }) => {
    const record = useRecordContext()
    switch (get(record, source)) {
        case 'In Progress':
        case 'Initializing':
            return (
                <span className={runPhaseClasses.initializing}>
                    In progress
                </span>
            )
        case 'Succeeded':
            return (
                <Grid container>
                    <Grid size={9}>Complete</Grid>
                    <Grid size={3}>
                        <CheckCircle sx={runPhaseClasses.success} />
                        {addLabel && record[source]}
                    </Grid>
                </Grid>
            )
        case 'Failed (Integration Issue)':
        case 'Failed (Platform Issue)':
        case 'Failed (Timeout)':
        case 'Failed (Refresh)':
        case 'Failed (Metadata Validation)':
            return (
                <Grid container sx={runPhaseClasses.error}>
                    <Grid sx={runPhaseClasses.status} size={9}>
                        Error
                    </Grid>
                    <Grid size={3}>
                        <Error />
                        {addLabel && record[source]}
                    </Grid>
                </Grid>
            )
        default:
            return (
                <span className={runPhaseClasses.initializing}>
                    Initializing
                </span>
            )
    }
}

const dataGridCSS = {
    headerCell: {
        height: '48px',
        fontSize: '20px',
        fontWeight: 'bold',
        color: 'black',
        paddingLeft: '8px',
    },
    rowCell: {
        height: '56px',
        textAlign: 'left',
        textTransform: 'capitalize',
        font: 'Roboto Regular',
        paddingLeft: '6px',
        fontSize: '16px',
    },
}

export const RunDataGrid = ({
    uidDisplay,
    bulkActionButtons,
    linkToTenant = true,
    ...rest
}) => {
    const { configs } = useThemeConfig()
    const { filterValues } = useListFilterContext()
    return (
        <Datagrid
            className={'pandium-logs-list'}
            {...rest}
            expand={RunExpand}
            expandSingle
            rowClick="expand"
            bulkActionButtons={false}
            empty={
                <EmptyState
                    emptyStateText={
                        Object.keys(filterValues ?? {}).length
                            ? 'No runs found matching the selected filters'
                            : 'No runs yet'
                    }
                />
            }
            sx={{
                '& .RaDatagrid-headerCell': dataGridCSS.headerCell,
                '& .RaDatagrid-rowCell': dataGridCSS.rowCell,
                '& .RaDatagrid-expandIconCell': {
                    '& .MuiIconButton-root': {
                        color: 'primary.main',
                        fontSize: '1.5rem',
                    },
                },
            }}
        >
            <IdField source="name" sortable={false} label={'ID'} />
            <TextField fontSize="16px" source="trigger" label="Trigger" />
            <FunctionField
                fontSize="16px"
                render={(record) =>
                    record?.createdDate &&
                    formatDate(record.createdDate, false, configs.dateFormat)
                }
                label="Created time"
                sortBy="createdDate"
                sortByOrder="DESC"
            />
            <FunctionField
                fontSize="16px"
                render={(record) =>
                    record?.completedDate &&
                    formatDate(record.completedDate, false, configs.dateFormat)
                }
                label="End time"
                sortBy="completedDate"
                sortByOrder="DESC"
            />
            <RunPhaseField source="status.phase" label="Status" />
        </Datagrid>
    )
}

export default ({ tenant, perPage = RUNS_PER_PAGE }) => {
    // Using one of RA's component to get necessary props to render ListView and Datagrid.
    const controllerProps = useListController({
        resource: 'runs',
        perPage: perPage,
        filter: { tenant_id: tenant.id },
        sort: { field: 'createdDate', order: 'DESC' },
    })

    // the parent component is polling the tenant. Here, we refresh the
    // list view whenever the tenant changes.
    const mounted = useRef(false)
    useEffect(() => {
        if (mounted.current) {
            controllerProps.refetch()
        } else {
            mounted.current = true
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [tenant])

    if (controllerProps.isLoading) {
        return <CircularProgress />
    }

    return (
        <ListContextProvider value={controllerProps}>
            <RunFilter />
            <RunDataGrid />
            <ListPagination />
        </ListContextProvider>
    )
}
